import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import ItemPager from "app/pages/sales-intelligence/common-components/item-pager/components/ItemPager";
import { LeadPriorityScore, type TriggeredRule } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";

import LeadScoreDefaultTooltipView from "../../components/LeadScoreDefaultTooltipView/LeadScoreDefaultTooltipView";
import LeadScorePagedTooltipView from "../../components/LeadScorePagedTooltipView/LeadScorePagedTooltipView";
import LeadScoreSummaryContent from "../../components/LeadScoreSummaryContent/LeadScoreSummaryContent";
import LeadScoreSummaryLoading from "../../components/LeadScoreSummaryLoading/LeadScoreSummaryLoading";
import useInitialSummaryAnimation from "../../hooks/useInitialSummaryAnimation";
import useLeadScoringSummaryQuery from "../../query/queries/useLeadScoringSummaryQuery";
import { getSortedTriggeredRules } from "../../utils/triggeredRules";

const ACTIVE_SCORES: ReadonlySet<LeadPriorityScore> = new Set([
    LeadPriorityScore.High,
    LeadPriorityScore.Medium,
    LeadPriorityScore.Low,
]);

const STATIC_SCORE_SUMMARY: Record<LeadPriorityScore, string | undefined> = {
    [LeadPriorityScore.NoScore]: "si.lead_score_agent.summary_text.default.no_score",
    [LeadPriorityScore.Unlock]: "si.lead_score_agent.summary_text.default.unlock",
    [LeadPriorityScore.Low]: undefined,
    [LeadPriorityScore.Medium]: undefined,
    [LeadPriorityScore.High]: undefined,
};

type Props = {
    triggeredRules: readonly TriggeredRule[];
    currentStep: number;
    fallbackScore: LeadPriorityScore;
    domain: string;
    onPrevious: VoidFunction;
    onNext: VoidFunction;
    onClose: VoidFunction;
};

const LeadScoreTooltipContainer = ({
    triggeredRules,
    currentStep,
    onPrevious,
    onNext,
    fallbackScore,
    domain,
    onClose,
}: Props) => {
    const { translate } = useTranslate();

    const sortedRules = useMemo(() => getSortedTriggeredRules(triggeredRules), [triggeredRules]);

    const currentRule = sortedRules[currentStep];
    const score = currentRule?.score ?? fallbackScore;
    const triggeredRuleId = currentRule?.ruleId ?? "";

    const isActive = ACTIVE_SCORES.has(score);
    const isSummaryEnabled = isActive && Boolean(triggeredRuleId);

    const { data, isFetching } = useLeadScoringSummaryQuery(
        {
            domain,
            score,
            triggeredRuleId,
        },
        isSummaryEnabled,
    );

    const isAnimateSummary = useInitialSummaryAnimation({ currentStep, isSummaryEnabled, isFetching });

    const { summary, businessContext } = data || {};
    const defaultSummary = STATIC_SCORE_SUMMARY[score] ? translate(STATIC_SCORE_SUMMARY[score]) : "";
    const businessContexts = businessContext ? [businessContext] : undefined;

    const isPaged = sortedRules.length > 1;

    const summaryBody =
        isFetching && isSummaryEnabled && !data ? (
            <LeadScoreSummaryLoading />
        ) : (
            <LeadScoreSummaryContent
                isAnimateSummary={isAnimateSummary}
                summary={summary || defaultSummary}
                businessContexts={businessContexts}
            />
        );

    if (isPaged) {
        return (
            <LeadScorePagedTooltipView
                score={score}
                domain={domain}
                onClose={onClose}
                pager={
                    <ItemPager
                        currentIndex={currentStep}
                        totalCount={sortedRules.length}
                        previousLabel={translate("si.common.button.back")}
                        nextLabel={translate("si.common.button.next")}
                        onPrevious={onPrevious}
                        onNext={onNext}
                    />
                }
            >
                {summaryBody}
            </LeadScorePagedTooltipView>
        );
    }

    return (
        <LeadScoreDefaultTooltipView
            score={score}
            domain={domain}
            onClose={onClose}
        >
            {summaryBody}
        </LeadScoreDefaultTooltipView>
    );
};

export default LeadScoreTooltipContainer;
